/**
 * MobileNav.js — mobile-only navigation (top header + side menu + bottom tabs).
 *
 * RENDER ONLY. All visibility logic lives in /config/accessControl.js — to
 * change which items a role sees, edit that config, never this file.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, Bell, BellRing, BellOff, Home, ChevronDown, ChevronRight,
  LogOut, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/utils';
import {
  isPushSupported, isPushSubscribed, subscribeToPush,
} from '../../utils/pushNotifications';
import { toast } from 'sonner';
import usePanelCounts from '../../hooks/usePanelCounts';
import useAccessOverrides from '../../hooks/useAccessOverrides';
import { getNavForRole } from '../../config/accessControl';

function badgeForPath(path, counts) {
  if (!path || !counts) return 0;
  if (path.startsWith('/task-management') || path === '/tasks') return counts.tasks;
  if (path === '/calendar' || path === '/book-meeting') return counts.meetings;
  if (path.startsWith('/inspections')) return counts.inspections;
  if (path === '/chat' || path === '/groups') return counts.chat;
  if (path === '/notifications') return counts.notifications;
  if (path.startsWith('/stock-transfers')) return counts.stock_transfers;
  if (path === '/approvals') return counts.approvals;
  return 0;
}

function MenuItemBtn({ item, location, counts, onNavigate }) {
  const Icon = item.icon;
  const isActive = location.pathname === item.path;
  const count = badgeForPath(item.path, counts);
  return (
    <button
      onClick={() => onNavigate(item.path)}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
        isActive ? 'bg-red-50 text-[#DC2626] border-r-4 border-[#DC2626]'
                 : 'text-slate-700 hover:bg-slate-50'
      }`}
      data-testid={`mobile-menu-${item.key}`}
    >
      <Icon size={20} />
      <span className="flex-1 font-medium">{item.label}</span>
      {count > 0 && (
        <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

function MenuDropdown({ dropdown, location, counts, onNavigate }) {
  const [open, setOpen] = useState(false);
  const Icon = dropdown.icon;
  const total = dropdown.items.reduce(
    (s, i) => s + (badgeForPath(i.path, counts) || 0), 0
  );
  return (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
        data-testid={`mobile-dropdown-${dropdown.key}`}
      >
        <Icon size={18} className="text-slate-500" />
        <span className="flex-1 font-bold text-sm text-slate-700">{dropdown.label}</span>
        {total > 0 && (
          <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center">
            {total > 99 ? '99+' : total}
          </span>
        )}
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && (
        <div className="bg-white">
          {dropdown.items.map(item => (
            <MenuItemBtn
              key={item.key}
              item={item}
              location={location}
              counts={counts}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MobileNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const counts = usePanelCounts();
  const overrides = useAccessOverrides();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupportedState] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    if (!user?.id) return;
    const fetchCount = async () => {
      try {
        const data = await apiRequest('/notifications/unread-count');
        setUnreadCount(data.count || 0);
      } catch (_) { /* silent */ }
    };
    fetchCount();
    const onUpdate = () => fetchCount();
    const onCleared = () => setUnreadCount(0);
    window.addEventListener('notifications-updated', onUpdate);
    window.addEventListener('notifications-cleared', onCleared);
    window.addEventListener('pie:badge-invalidate', onUpdate);
    const interval = setInterval(fetchCount, 60_000);

    setPushSupportedState(isPushSupported());
    if (isPushSupported()) isPushSubscribed().then(setPushEnabled);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-updated', onUpdate);
      window.removeEventListener('notifications-cleared', onCleared);
      window.removeEventListener('pie:badge-invalidate', onUpdate);
    };
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      apiRequest('/notifications/unread-count')
        .then(d => setUnreadCount(d.count || 0))
        .catch(() => {});
    }
  }, [location.pathname, user?.id]);

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      const token = localStorage.getItem('token');
      await subscribeToPush(token);
      setPushEnabled(true);
      toast.success('Push notifications enabled!');
    } catch (e) {
      toast.error(e?.message?.includes('permission denied')
        ? 'Please allow notifications in your browser'
        : 'Failed to enable notifications');
    } finally {
      setEnablingPush(false);
    }
  };

  const handleNavClick = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const role = user?.role;
  const { common, dropdowns, issueReporting } = role
    ? getNavForRole(role, overrides, { isFranchiseStore: !!user?.is_franchise_store })
    : { common: [], dropdowns: [], issueReporting: null };

  // Bottom tabs — derived from common items so they stay in sync.
  const bottomTabs = [
    { path: '/admin/my-dashboard', icon: Home,           label: 'Home',     key: 'home' },
    common.find(i => i.key === 'tasks'),
    common.find(i => i.key === 'chat'),
    common.find(i => i.key === 'profile'),
  ].filter(Boolean);

  return (
    <>
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40 lg:hidden">
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 -ml-2 hover:bg-slate-100 rounded-lg"
          data-testid="menu-toggle"
        >
          <Menu size={24} className="text-slate-700" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="p-1 -mx-1 rounded-md hover:bg-slate-100 active:bg-slate-200"
          aria-label="Go to dashboard"
          data-testid="mobile-logo-btn"
        >
          <img src="/pie-logo.png" alt="PIE" className="h-10 pointer-events-none" />
        </button>

        <div className="flex items-center gap-1">
          {pushSupported && (pushEnabled ? (
            <button
              className="p-2 hover:bg-slate-100 rounded-lg text-green-600"
              title="Push notifications enabled"
              data-testid="mobile-push-enabled"
            >
              <BellRing size={22} />
            </button>
          ) : (
            <button
              onClick={handleEnablePush}
              disabled={enablingPush}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
              title="Enable push notifications"
              data-testid="mobile-enable-push"
            >
              {enablingPush ? <Bell size={22} className="animate-pulse" /> : <BellOff size={22} />}
            </button>
          ))}

          <button
            onClick={() => navigate('/notifications')}
            className="p-2 -mr-2 hover:bg-slate-100 rounded-lg relative"
            data-testid="mobile-notifications-button"
          >
            <Bell size={24} className={unreadCount > 0 ? 'text-[#DC2626]' : 'text-slate-700'} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-5 w-5 bg-[#DC2626] text-white text-[10px] font-bold items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Side Menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setMenuOpen(false)}
          style={{ touchAction: 'none' }}
        >
          <div
            className="absolute top-0 left-0 bottom-0 w-80 bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ touchAction: 'auto' }}
          >
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b bg-slate-50">
              <button
                onClick={() => handleNavClick('/profile')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                data-testid="mobile-menu-profile-link"
              >
                <div className="w-10 h-10 rounded-full bg-[#DC2626] text-white flex items-center justify-center font-semibold">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-sm text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
                </div>
              </button>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 hover:bg-slate-200 rounded-full"
                data-testid="mobile-menu-close"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2 overscroll-contain">
              {common.map(item => (
                <MenuItemBtn
                  key={item.key}
                  item={item}
                  location={location}
                  counts={counts}
                  onNavigate={handleNavClick}
                />
              ))}

              {dropdowns.map(d => (
                <MenuDropdown
                  key={d.key}
                  dropdown={d}
                  location={location}
                  counts={counts}
                  onNavigate={handleNavClick}
                />
              ))}

              {issueReporting && (
                <MenuItemBtn
                  item={issueReporting}
                  location={location}
                  counts={counts}
                  onNavigate={handleNavClick}
                />
              )}
            </div>

            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="flex items-center gap-3 px-4 py-3 text-red-600 border-t hover:bg-red-50"
              data-testid="mobile-menu-logout"
            >
              <LogOut size={20} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Bottom Tabs */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-slate-200 flex z-40 lg:hidden">
        {bottomTabs.map(tab => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.path;
          const count = badgeForPath(tab.path, counts);
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`flex-1 flex flex-col items-center justify-center relative ${
                isActive ? 'text-[#DC2626]' : 'text-slate-500'
              }`}
              data-testid={`mobile-tab-${tab.key}`}
            >
              <Icon size={22} />
              <span className="text-[10px] font-medium mt-0.5">{tab.label}</span>
              {count > 0 && (
                <span className="absolute top-1 right-[26%] h-4 min-w-[16px] px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center">
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </>
  );
}
